import { useState } from "react";
import { BadgeCheck, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const S = { PASS: "text-emerald-300", WARN: "text-amber-300", FAIL: "text-rose-300" };
const fmt = (v) => (typeof v === "object" && v !== null ? JSON.stringify(v) : String(v ?? "—"));

export function StripeReadinessCard() {
  const { user } = useAuth();
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!user?.is_admin) return null;
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/billing/live-readiness");
      setRes(r.data);
      toast.success(`Stripe checks: ${r.data.summary.pass} pass · ${r.data.summary.warn} warn · ${r.data.summary.fail} fail`);
    } catch (e) {
      toast.error(apiError(e, "Readiness check failed"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="apx-card p-5 space-y-3" data-testid="stripe-readiness-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-semibold text-lg flex items-center gap-2"><BadgeCheck className="w-4 h-4 text-cyan-300" /> Stripe — live launch checklist</h2>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">Read-only checks against the configured Stripe account ({res?.mode || "…"} mode): account status, EV-IDEX branding and statement descriptor, the four prices, webhook endpoint and event delivery, Customer Portal features, and a $0 checkout probe (session created then expired immediately — no charge).</p>
        </div>
        <button type="button" onClick={run} disabled={busy} data-testid="stripe-readiness-run-btn" className="btn-ghost border border-slate-700 rounded-lg px-3 py-1.5 text-xs inline-flex items-center gap-1.5 shrink-0">{busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldAlert className="w-3.5 h-3.5" />} Run checks</button>
      </div>
      {res && (
        <div className="space-y-2" data-testid="stripe-readiness-results">
          <div className="text-xs text-slate-400" data-testid="stripe-readiness-summary">Account {res.account.id} · {res.account.country?.toUpperCase()} · {res.account.livemode ? "LIVE" : "TEST"} · business name “{res.account.name || "—"}” · descriptor “{res.account.statement_descriptor || "—"}” · <span className="text-emerald-300">{res.summary.pass} pass</span> · <span className="text-amber-300">{res.summary.warn} warn</span> · <span className={res.summary.fail ? "text-rose-300" : "text-slate-300"}>{res.summary.fail} fail</span></div>
          <div className="tbl-scroll overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="text-slate-500 text-left"><th className="py-1 pr-3">Status</th><th className="py-1 pr-3">Check</th><th className="py-1 pr-3">Value</th><th className="py-1">How to fix (Stripe Dashboard)</th></tr></thead>
              <tbody>{res.rows.map((r, i) => (
                <tr key={i} className="border-t border-slate-800/60" data-testid={`stripe-check-${r.status.toLowerCase()}-${i}`}>
                  <td className={`py-1.5 pr-3 font-semibold ${S[r.status]}`}>{r.status}</td>
                  <td className="py-1.5 pr-3 text-slate-200">{r.check}</td>
                  <td className="py-1.5 pr-3 text-slate-400 max-w-[28rem] truncate" title={fmt(r.value)}>{fmt(r.value)}</td>
                  <td className="py-1.5 text-slate-400">{r.fix}</td>
                </tr>))}</tbody>
            </table>
          </div>
          <div className="text-xs text-slate-400" data-testid="stripe-readiness-features">Feature access by plan — Pro: {Object.entries(res.features.gating).filter(([, p]) => p === "pro").map(([f]) => f).join(", ")} · Premium adds: {Object.entries(res.features.gating).filter(([, p]) => p === "premium").map(([f]) => f).join(", ")} · users: free {res.features.users_by_plan.free}, pro {res.features.users_by_plan.pro}, premium {res.features.users_by_plan.premium}</div>
        </div>
      )}
    </section>
  );
}
